/** Persistent debug logger — writes to localStorage for post-mortem review.
 *
 *  Usage in code:  import { debugLog } from '../utils/debug-log'
 *                  debugLog.info('wizard', 'profile created', { id })
 *
 *  Review in browser console:
 *    debugLog.dump()         — print all entries
 *    debugLog.dump('wizard') — filter by tag
 *    debugLog.clear()        — clear log
 */

const STORAGE_KEY = 'aegis_debug_log'
const MAX_ENTRIES = 300

interface LogEntry {
  ts: string
  level: 'info' | 'warn' | 'error'
  tag: string
  msg: string
  data?: unknown
}

function getEntries(): LogEntry[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  } catch {
    return []
  }
}

function append(entry: LogEntry) {
  const entries = getEntries()
  entries.push(entry)
  // Prune oldest if over limit
  while (entries.length > MAX_ENTRIES) entries.shift()
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
}

function log(level: LogEntry['level'], tag: string, msg: string, data?: unknown) {
  const entry: LogEntry = {
    ts: new Date().toISOString(),
    level,
    tag,
    msg,
    ...(data !== undefined ? { data } : {}),
  }
  append(entry)

  // Also log to console in development
  const prefix = `[${tag}]`
  if (level === 'error') console.error(prefix, msg, data ?? '')
  else if (level === 'warn') console.warn(prefix, msg, data ?? '')
  else console.log(prefix, msg, data ?? '')
}

export const debugLog = {
  info: (tag: string, msg: string, data?: unknown) => log('info', tag, msg, data),
  warn: (tag: string, msg: string, data?: unknown) => log('warn', tag, msg, data),
  error: (tag: string, msg: string, data?: unknown) => log('error', tag, msg, data),

  /** Print log entries to console. Optionally filter by tag. */
  dump: (filterTag?: string) => {
    const entries = getEntries()
    const filtered = filterTag ? entries.filter((e) => e.tag === filterTag) : entries
    console.table(
      filtered.map((e) => ({
        time: e.ts.slice(11, 23),
        level: e.level,
        tag: e.tag,
        msg: e.msg,
        data: e.data ? JSON.stringify(e.data) : '',
      })),
    )
    return filtered
  },

  /** Clear all log entries. */
  clear: () => localStorage.removeItem(STORAGE_KEY),
}

// Expose on window for console access
if (typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).debugLog = debugLog
}
